import React, { useEffect, useMemo, useRef, useState } from 'react';

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridToolbar,
} from '@mui/x-data-grid';

import {
  InfiniteApplicationActionBody,
  InfiniteApplicationApplicant,
  InfiniteApplicationStatus,
  InfiniteApplicationSummary,
  InfiniteEligibility,
  UpdateInfiniteEligibilityInput,
  actionInfiniteApplication,
  listInfiniteApplications,
  listInfiniteEligibilities,
  updateInfiniteEligibility,
} from '~/services/LuxLoyaltyInfiniteApplicationService';
import { formatDateDDMonYY } from '~/services/TimeService';

import ApplicationActionConfirmDialog from './ApplicationActionConfirmDialog';
import BulkMarkEmailFlagDialog from './BulkMarkEmailFlagDialog';
import EligibleUsersDialog from './EligibleUsersDialog';
import InfiniteApplicationEnrolmentDialog from './InfiniteApplicationEnrolmentDialog';
import ReviewApplicationDialog, {
  InfiniteApplicationEnrolmentRequest,
  PendingApplicationAction,
} from './ReviewApplicationDialog';
import {
  STATUS_LABELS,
  StatusChip,
  appendApplicationNote,
  getAddress,
  getDisplayName,
  getPhone,
} from './infiniteApplicationReview.utils';
import {
  BulkEligibilityEmailFlagPlan,
  EligibilityEmailFlagField,
  getBulkEligibilityEmailFlagConfirmMessage,
  getBulkEligibilityEmailFlagEmptyMessage,
  getBulkEligibilityEmailFlagResultMessage,
  getChangedEligibilityEmailFlags,
  getSelectedRowsById,
  planBulkEligibilityEmailFlagUpdate,
} from './infiniteEligibilityBulkUpdate';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
type EligibilityStatusFilter = 'all' | 'active' | 'retracted';
type ApplicationStatusFilter = 'all' | InfiniteApplicationStatus;
type InfiniteApplicationsPageView = 'applications' | 'eligible-users';

const ACTION_SUCCESS_MESSAGES: Record<InfiniteApplicationActionBody['action'], string> = {
  APPROVE: 'Application approved',
  REJECT: 'Application rejected',
  REOPEN: 'Application reopened',
  FINALIZE: 'Application finalized',
};

const ACTION_NOTE_LABELS: Record<PendingApplicationAction['action'], string> = {
  APPROVE: 'Approval note',
  REJECT: 'Rejection note',
  REOPEN: 'Reopen note',
};

type InfiniteApplicationGridRow = {
  id: string;
  application: InfiniteApplicationSummary;
  primaryApplicant: InfiniteApplicationApplicant;
  primaryTraveller: string;
  email: string;
  phone: string;
  address: string;
  applied: string;
  status: InfiniteApplicationStatus;
  statusLabel: string;
};

type InfiniteEligibilityGridRow = InfiniteEligibility & {
  name: string;
  phoneDisplay: string;
  statusLabel: string;
  expires: string;
  created: string;
  updated: string;
  associatedApplicationCount: number;
  finalizedApplicationCount: number;
};

function getInitials(person: Pick<InfiniteApplicationApplicant, 'firstName' | 'lastName'>): string {
  return `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase() || '?';
}

function getEligibilityPhone(eligibility: Pick<InfiniteEligibility, 'phonePrefix' | 'phone'>): string {
  return [eligibility.phonePrefix, eligibility.phone].filter(Boolean).join(' ');
}

function countLabel(count: number, label: string): string {
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}

function hasFinalizedAssociatedApplication(
  eligibility: Pick<InfiniteEligibilityGridRow, 'finalizedApplicationCount'>,
): boolean {
  return eligibility.finalizedApplicationCount > 0;
}

function EligibilityEmailSentCheckbox({
  checked,
  disabled,
  label,
  onToggle,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onToggle: (checked: boolean) => void;
}) {
  return (
    <Checkbox
      checked={checked}
      disabled={disabled}
      size="small"
      inputProps={{ 'aria-label': label }}
      onClick={(event) => event.stopPropagation()}
      onChange={(_, nextChecked) => onToggle(nextChecked)}
    />
  );
}

type EditEligibilityFormState = {
  firstName: string;
  lastName: string;
  email: string;
  phonePrefix: string;
  phone: string;
  isRetracted: boolean;
  registerInterestEmailSent: boolean;
  conciergeIntroEmailSent: boolean;
};

function buildEditEligibilityForm(eligibility: InfiniteEligibility): EditEligibilityFormState {
  return {
    firstName: eligibility.firstName,
    lastName: eligibility.lastName,
    email: eligibility.email,
    phonePrefix: eligibility.phonePrefix ?? '',
    phone: eligibility.phone ?? '',
    isRetracted: eligibility.isRetracted,
    registerInterestEmailSent: !!eligibility.registerInterestEmailSent,
    conciergeIntroEmailSent: !!eligibility.conciergeIntroEmailSent,
  };
}

function EditEligibilityDialog({
  eligibility,
  open,
  isSubmitting,
  onClose,
  onSubmit,
}: {
  eligibility: InfiniteEligibilityGridRow | null;
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (eligibility: InfiniteEligibilityGridRow, body: UpdateInfiniteEligibilityInput) => void;
}) {
  const [form, setForm] = useState<EditEligibilityFormState | null>(null);

  useEffect(() => {
    setForm(eligibility ? buildEditEligibilityForm(eligibility) : null);
  }, [eligibility]);

  const updateForm = (patch: Partial<EditEligibilityFormState>) => {
    setForm((current) => (current ? { ...current, ...patch } : current));
  };

  const canSubmit =
    !!eligibility &&
    !!form &&
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    form.email.trim().length > 0;

  const submit = () => {
    if (!eligibility || !form || !canSubmit) {
      return;
    }

    onSubmit(eligibility, {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim().toLowerCase(),
      phonePrefix: form.phonePrefix.trim() || null,
      phone: form.phone.trim() || null,
      isRetracted: form.isRetracted,
      ...getChangedEligibilityEmailFlags(eligibility, {
        registerInterestEmailSent: form.registerInterestEmailSent,
        conciergeIntroEmailSent: form.conciergeIntroEmailSent,
      }),
    });
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit eligible user</DialogTitle>
      <DialogContent dividers>
        {form && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={form.firstName}
                onChange={(event) => updateForm({ firstName: event.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Last name"
                value={form.lastName}
                onChange={(event) => updateForm({ lastName: event.target.value })}
                fullWidth
                required
              />
            </Stack>
            <TextField
              label="Email"
              value={form.email}
              onChange={(event) => updateForm({ email: event.target.value })}
              fullWidth
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Phone prefix"
                value={form.phonePrefix}
                onChange={(event) => updateForm({ phonePrefix: event.target.value })}
                sx={{ minWidth: { sm: 160 } }}
              />
              <TextField
                label="Phone"
                value={form.phone}
                onChange={(event) => updateForm({ phone: event.target.value })}
                fullWidth
              />
            </Stack>
            <TextField
              select
              label="Status"
              value={form.isRetracted ? 'retracted' : 'active'}
              onChange={(event) => updateForm({ isRetracted: event.target.value === 'retracted' })}
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="retracted">Retracted</MenuItem>
            </TextField>
            <Stack spacing={0.5} sx={{ '& .MuiFormControlLabel-root': { ml: 0 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.registerInterestEmailSent}
                    onChange={(_, checked) => updateForm({ registerInterestEmailSent: checked })}
                  />
                }
                label="Register interest email sent"
              />
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.conciergeIntroEmailSent}
                      disabled={!eligibility || !hasFinalizedAssociatedApplication(eligibility)}
                      onChange={(_, checked) => updateForm({ conciergeIntroEmailSent: checked })}
                    />
                  }
                  label="Concierge intro sent"
                />
                {eligibility && !hasFinalizedAssociatedApplication(eligibility) && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4 }}>
                    Available once a related application is finalized.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit || isSubmitting}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const BULK_EMAIL_FLAG_TITLES: Record<EligibilityEmailFlagField, string> = {
  registerInterestEmailSent: 'Mark register interest email sent',
  conciergeIntroEmailSent: 'Mark concierge intro sent',
};

function BulkEligibilityEmailConfirmDialog({
  plan,
  open,
  isSubmitting,
  onClose,
  onConfirm,
}: {
  plan: BulkEligibilityEmailFlagPlan | null;
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (plan: BulkEligibilityEmailFlagPlan) => void;
}) {
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{plan ? BULK_EMAIL_FLAG_TITLES[plan.field] : 'Update selected eligible users'}</DialogTitle>
      <DialogContent dividers>
        {plan && <Typography>{getBulkEligibilityEmailFlagConfirmMessage(plan)}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            if (plan) {
              onConfirm(plan);
            }
          }}
          disabled={!plan || isSubmitting}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssociatedApplicationsDialog({
  eligibility,
  open,
  onClose,
  onReviewApplication,
}: {
  eligibility: InfiniteEligibilityGridRow | null;
  open: boolean;
  onClose: () => void;
  onReviewApplication: (applicationId: string) => void;
}) {
  const associatedApplications = eligibility?.potentialAssociatedApplications ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Potential associated applications</DialogTitle>
      <DialogContent dividers>
        {eligibility && (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {eligibility.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {eligibility.email}
              </Typography>
            </Box>
            {associatedApplications.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Applicant</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Application created</TableCell>
                      <TableCell>Application ID</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {associatedApplications.map((application) => (
                      <TableRow key={`${application.applicationId}-${application.applicantId}`}>
                        <TableCell>
                          {[application.applicantFirstName, application.applicantLastName].filter(Boolean).join(' ')}
                        </TableCell>
                        <TableCell>{application.applicantEmail}</TableCell>
                        <TableCell>{application.applicantRole}</TableCell>
                        <TableCell>
                          <StatusChip status={application.applicationStatus} />
                        </TableCell>
                        <TableCell>{formatDateDDMonYY(application.applicationCreatedAt)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {application.applicationId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onReviewApplication(application.applicationId)}
                          >
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No potential associated applications.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InfiniteApplicationsPage({ view }: { view: InfiniteApplicationsPageView }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [applicationsEmailFilter, setApplicationsEmailFilter] = useState('');
  const [applicationsStatusFilter, setApplicationsStatusFilter] = useState<ApplicationStatusFilter>('all');
  const [eligibilitiesPage, setEligibilitiesPage] = useState(1);
  const [eligibilitiesPageSize, setEligibilitiesPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [eligibilitiesEmailFilter, setEligibilitiesEmailFilter] = useState('');
  const [eligibilitiesStatusFilter, setEligibilitiesStatusFilter] = useState<EligibilityStatusFilter>('all');
  const [reviewApplicationId, setReviewApplicationId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingApplicationAction | null>(null);
  const [enrolmentRequest, setEnrolmentRequest] = useState<InfiniteApplicationEnrolmentRequest | null>(null);
  const [isEligibleUsersDialogOpen, setIsEligibleUsersDialogOpen] = useState(false);
  const [isBulkMarkEmailFlagDialogOpen, setIsBulkMarkEmailFlagDialogOpen] = useState(false);
  const [editingEligibility, setEditingEligibility] = useState<InfiniteEligibilityGridRow | null>(null);
  const [associatedApplicationsEligibility, setAssociatedApplicationsEligibility] =
    useState<InfiniteEligibilityGridRow | null>(null);
  const [eligibilitySelectionModel, setEligibilitySelectionModel] = useState<GridRowSelectionModel>([]);
  const [pendingBulkEmailUpdate, setPendingBulkEmailUpdate] = useState<BulkEligibilityEmailFlagPlan | null>(null);
  const applicationsRowCountRef = useRef(0);
  const eligibilitiesRowCountRef = useRef(0);
  const applicationIdParam = searchParams.get('applicationId');

  const applicationsQuery = useQuery({
    queryKey: [
      'lux-loyalty',
      'infinite-applications',
      page,
      pageSize,
      applicationsEmailFilter,
      applicationsStatusFilter,
    ],
    queryFn: () =>
      listInfiniteApplications({
        page,
        pageSize,
        email: applicationsEmailFilter.trim() || undefined,
        status: applicationsStatusFilter === 'all' ? undefined : applicationsStatusFilter,
      }),
    enabled: view === 'applications',
    placeholderData: keepPreviousData,
  });
  const eligibilitiesQuery = useQuery({
    queryKey: [
      'lux-loyalty',
      'infinite-eligible-users',
      eligibilitiesPage,
      eligibilitiesPageSize,
      eligibilitiesEmailFilter,
      eligibilitiesStatusFilter,
    ],
    queryFn: () =>
      listInfiniteEligibilities({
        page: eligibilitiesPage,
        pageSize: eligibilitiesPageSize,
        email: eligibilitiesEmailFilter.trim() || undefined,
        isRetracted: eligibilitiesStatusFilter === 'all' ? undefined : eligibilitiesStatusFilter === 'retracted',
      }),
    enabled: view === 'eligible-users',
    placeholderData: keepPreviousData,
  });

  const applications = applicationsQuery.data?.result.applications ?? [];
  const pageInfo = applicationsQuery.data?.result.pageInfo;
  if (pageInfo) {
    applicationsRowCountRef.current = pageInfo.totalRecords;
  }
  const totalRecords = pageInfo?.totalRecords ?? applicationsRowCountRef.current;
  const eligibilities = eligibilitiesQuery.data?.result.eligibilities ?? [];
  const eligibilitiesPageInfo = eligibilitiesQuery.data?.result.pageInfo;
  if (eligibilitiesPageInfo) {
    eligibilitiesRowCountRef.current = eligibilitiesPageInfo.totalRecords;
  }
  const eligibilitiesTotalRecords = eligibilitiesPageInfo?.totalRecords ?? eligibilitiesRowCountRef.current;
  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: page - 1,
      pageSize,
    }),
    [page, pageSize],
  );
  const eligibilitiesPaginationModel = useMemo<GridPaginationModel>(
    () => ({
      page: eligibilitiesPage - 1,
      pageSize: eligibilitiesPageSize,
    }),
    [eligibilitiesPage, eligibilitiesPageSize],
  );

  const invalidateInfiniteApplicationReviewQueries = async (applicationId?: string | null) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-applications'] }),
      applicationId
        ? queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-application', applicationId] })
        : Promise.resolve(),
      queryClient.invalidateQueries({ queryKey: ['users', 'infinite-application-email'] }),
      queryClient.invalidateQueries({ queryKey: ['users', 'infinite-application-account-details'] }),
      queryClient.invalidateQueries({ queryKey: ['loyalty-account'] }),
    ]);
  };

  const actionMutation = useMutation({
    mutationFn: ({ applicationId, body }: { applicationId: string; body: InfiniteApplicationActionBody }) =>
      actionInfiniteApplication(applicationId, body),
    onSuccess: async (_, variables) => {
      enqueueSnackbar(ACTION_SUCCESS_MESSAGES[variables.body.action], { variant: 'success' });
      setPendingAction(null);
      await invalidateInfiniteApplicationReviewQueries(variables.applicationId);
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
  const updateEligibilityMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateInfiniteEligibilityInput; closeEditor?: boolean }) =>
      updateInfiniteEligibility(id, body),
    onSuccess: async (_, variables) => {
      enqueueSnackbar('Eligible user updated', { variant: 'success' });
      if (variables.closeEditor) {
        setEditingEligibility(null);
      }
      await queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-eligible-users'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
  const bulkUpdateEligibilityEmailFlagsMutation = useMutation({
    mutationFn: async (plan: BulkEligibilityEmailFlagPlan) => {
      const results = await Promise.allSettled(
        plan.updates.map((update) => updateInfiniteEligibility(update.id, update.body)),
      );
      const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');

      return {
        updatedCount: results.length - failedResults.length,
        failedCount: failedResults.length,
        skippedIneligibleCount: plan.skippedIneligibleCount,
        firstError: failedResults[0]?.reason,
      };
    },
    onSuccess: async (result) => {
      enqueueSnackbar(getBulkEligibilityEmailFlagResultMessage(result), {
        variant: result.failedCount > 0 ? 'warning' : 'success',
      });
      if (result.firstError instanceof Error) {
        enqueueSnackbar(result.firstError.message, { variant: 'error' });
      }
      setPendingBulkEmailUpdate(null);
      setEligibilitySelectionModel([]);
      await queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-eligible-users'] });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });
  const isUpdatingEligibility =
    updateEligibilityMutation.isPending || bulkUpdateEligibilityEmailFlagsMutation.isPending;

  const isMutating = actionMutation.isPending;

  useEffect(() => {
    if (view === 'applications' && applicationIdParam && reviewApplicationId !== applicationIdParam) {
      setReviewApplicationId(applicationIdParam);
    }
  }, [applicationIdParam, reviewApplicationId, view]);

  const openReviewApplication = (applicationId: string) => {
    setReviewApplicationId(applicationId);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('applicationId', applicationId);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const closeReviewApplication = () => {
    setReviewApplicationId(null);
    if (!searchParams.has('applicationId')) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('applicationId');
    setSearchParams(nextSearchParams, { replace: true });
  };
  const rows = useMemo<Array<InfiniteApplicationGridRow>>(
    () =>
      applications.flatMap((application) => {
        const primaryApplicant = application.primaryApplicant;
        if (!primaryApplicant) {
          return [];
        }

        return [
          {
            id: application.id,
            application,
            primaryApplicant,
            primaryTraveller: getDisplayName(primaryApplicant),
            email: primaryApplicant.email,
            phone: getPhone(primaryApplicant),
            address: getAddress(primaryApplicant),
            applied: formatDateDDMonYY(application.createdAt),
            status: application.status,
            statusLabel: STATUS_LABELS[application.status],
          },
        ];
      }),
    [applications],
  );
  const eligibilityRows = useMemo<Array<InfiniteEligibilityGridRow>>(
    () =>
      eligibilities.map((eligibility) => ({
        ...eligibility,
        name: [eligibility.firstName, eligibility.lastName].filter(Boolean).join(' '),
        phoneDisplay: getEligibilityPhone(eligibility),
        statusLabel: eligibility.isRetracted ? 'Retracted' : 'Active',
        expires: eligibility.expiresAt ? formatDateDDMonYY(eligibility.expiresAt) : '-',
        created: formatDateDDMonYY(eligibility.createdAt),
        updated: formatDateDDMonYY(eligibility.updatedAt),
        associatedApplicationCount: eligibility.potentialAssociatedApplications.length,
        finalizedApplicationCount: eligibility.potentialAssociatedApplications.filter(
          (application) => application.applicationStatus === 'FINALIZED',
        ).length,
      })),
    [eligibilities],
  );
  const selectedEligibilityRows = useMemo(
    () => getSelectedRowsById(eligibilityRows, eligibilitySelectionModel),
    [eligibilityRows, eligibilitySelectionModel],
  );
  const selectedConciergeEligibleCount = selectedEligibilityRows.filter(hasFinalizedAssociatedApplication).length;

  const startBulkEligibilityEmailUpdate = (field: EligibilityEmailFlagField) => {
    const plan = planBulkEligibilityEmailFlagUpdate(selectedEligibilityRows, field, true);
    if (plan.updates.length === 0) {
      enqueueSnackbar(getBulkEligibilityEmailFlagEmptyMessage(plan), { variant: 'info' });
      return;
    }

    setPendingBulkEmailUpdate(plan);
  };

  const confirmApplicationAction = (action: PendingApplicationAction, notes?: string) => {
    const updatedNotes = appendApplicationNote(action.currentNotes, notes, ACTION_NOTE_LABELS[action.action]);
    const body: InfiniteApplicationActionBody =
      action.action === 'REJECT'
        ? { action: 'REJECT', notes: updatedNotes ?? action.currentNotes?.trim() ?? '' }
        : { action: action.action, ...(updatedNotes ? { notes: updatedNotes } : {}) };

    actionMutation.mutate({
      applicationId: action.applicationId,
      body,
    });
  };

  const finalizeApplication = (
    applicationId: string,
    applicantCustomerMappings: Array<{ applicationApplicantId: string; customerId: string }>,
    notes?: string,
  ) => {
    const trimmedNotes = notes?.trim();
    actionMutation.mutate({
      applicationId,
      body: {
        action: 'FINALIZE',
        applicantCustomerMappings,
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
      },
    });
  };
  const columns = useMemo<Array<GridColDef<InfiniteApplicationGridRow>>>(
    () => [
      {
        field: 'primaryTraveller',
        headerName: 'Primary traveller',
        minWidth: 260,
        flex: 1.2,
        renderCell: ({ row }: GridRenderCellParams<InfiniteApplicationGridRow>) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.900', fontSize: 14 }}>
              {getInitials(row.primaryApplicant)}
            </Avatar>
            <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap title={row.primaryTraveller}>
              {row.primaryTraveller}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        minWidth: 320,
        flex: 1.4,
        renderCell: ({ row }: GridRenderCellParams<InfiniteApplicationGridRow>) => (
          <Typography noWrap title={row.email}>
            {row.email}
          </Typography>
        ),
      },
      { field: 'phone', headerName: 'Phone', minWidth: 160, flex: 0.7 },
      {
        field: 'address',
        headerName: 'Address',
        minWidth: 240,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<InfiniteApplicationGridRow>) => (
          <Typography noWrap title={row.address}>
            {row.address || '-'}
          </Typography>
        ),
      },
      { field: 'applied', headerName: 'Applied', minWidth: 130, flex: 0.5 },
      {
        field: 'statusLabel',
        headerName: 'Status',
        minWidth: 140,
        flex: 0.5,
        renderCell: ({ row }: GridRenderCellParams<InfiniteApplicationGridRow>) => <StatusChip status={row.status} />,
      },
      {
        field: 'account',
        headerName: 'Action',
        minWidth: 300,
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<InfiniteApplicationGridRow>) => (
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 1 }}>
            <Button
              size="small"
              variant={row.application.status === 'APPROVED' ? 'contained' : 'outlined'}
              color={row.application.status === 'APPROVED' ? 'success' : 'inherit'}
              onClick={() => openReviewApplication(row.application.id)}
            >
              {row.application.status === 'APPROVED' ? 'Action now' : 'Review'}
            </Button>
            {(row.application.status === 'SUBMITTED' || row.application.status === 'REJECTED') && (
              <Button
                size="small"
                color={row.application.status === 'REJECTED' ? 'primary' : 'error'}
                disabled={isMutating}
                onClick={() =>
                  setPendingAction({
                    action: row.application.status === 'REJECTED' ? 'REOPEN' : 'REJECT',
                    applicationId: row.application.id,
                    applicant: row.primaryApplicant,
                    currentNotes: row.application.notes,
                  })
                }
              >
                {row.application.status === 'REJECTED' ? 'Reopen' : 'Reject'}
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [isMutating],
  );
  const eligibilityColumns = useMemo<Array<GridColDef<InfiniteEligibilityGridRow>>>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <Typography noWrap title={row.name}>
            {row.name}
          </Typography>
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        minWidth: 320,
        flex: 1.4,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <Typography noWrap title={row.email}>
            {row.email}
          </Typography>
        ),
      },
      { field: 'phoneDisplay', headerName: 'Phone', minWidth: 160, flex: 0.7 },
      {
        field: 'statusLabel',
        headerName: 'Status',
        minWidth: 100,
        flex: 0.35,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <Typography color={row.isRetracted ? 'error.main' : 'success.main'}>{row.statusLabel}</Typography>
        ),
      },
      {
        field: 'registerInterestEmailSent',
        headerName: 'Register interest email sent',
        minWidth: 220,
        flex: 0.7,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <EligibilityEmailSentCheckbox
            checked={!!row.registerInterestEmailSent}
            disabled={isUpdatingEligibility}
            label="Register interest email sent"
            onToggle={(checked) =>
              updateEligibilityMutation.mutate({
                id: row.id,
                body: { registerInterestEmailSent: checked },
              })
            }
          />
        ),
      },
      {
        field: 'conciergeIntroEmailSent',
        headerName: 'Concierge intro sent',
        minWidth: 180,
        flex: 0.6,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) =>
          hasFinalizedAssociatedApplication(row) ? (
            <EligibilityEmailSentCheckbox
              checked={!!row.conciergeIntroEmailSent}
              disabled={isUpdatingEligibility}
              label="Concierge intro sent"
              onToggle={(checked) =>
                updateEligibilityMutation.mutate({
                  id: row.id,
                  body: { conciergeIntroEmailSent: checked },
                })
              }
            />
          ) : (
            <Typography color="text.secondary">-</Typography>
          ),
      },
      {
        field: 'actions',
        headerName: 'Action',
        minWidth: 90,
        flex: 0.3,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <Button size="small" variant="outlined" onClick={() => setEditingEligibility(row)}>
            Edit
          </Button>
        ),
      },
      { field: 'expires', headerName: 'Expires', minWidth: 130, flex: 0.5 },
      { field: 'created', headerName: 'Created', minWidth: 130, flex: 0.5 },
      { field: 'updated', headerName: 'Updated', minWidth: 130, flex: 0.5 },
      {
        field: 'associatedApplicationCount',
        headerName: 'Related applications',
        minWidth: 190,
        flex: 0.7,
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<InfiniteEligibilityGridRow>) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                {countLabel(row.associatedApplicationCount, 'application')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.finalizedApplicationCount} finalized
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="outlined"
              disabled={row.associatedApplicationCount === 0}
              onClick={() => setAssociatedApplicationsEligibility(row)}
            >
              View
            </Button>
          </Stack>
        ),
      },
    ],
    [isUpdatingEligibility, updateEligibilityMutation],
  );

  return (
    <Box>
      {view === 'applications' && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Applications
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 1200 }}>
            Primary travellers who have applied. Review an application to see all applicants and booking authorities.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search email"
              value={applicationsEmailFilter}
              onChange={(event) => {
                setApplicationsEmailFilter(event.target.value);
                setPage(1);
              }}
              sx={{ minWidth: { md: 320 } }}
            />
            <TextField
              select
              label="Status"
              value={applicationsStatusFilter}
              onChange={(event) => {
                setApplicationsStatusFilter(event.target.value as ApplicationStatusFilter);
                setPage(1);
              }}
              sx={{ minWidth: { md: 180 } }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="SUBMITTED">Submitted</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="FINALIZED">Finalized</MenuItem>
            </TextField>
          </Stack>

          {applicationsQuery.error instanceof Error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {applicationsQuery.error.message}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={applicationsQuery.isLoading || applicationsQuery.isPlaceholderData}
              pagination
              paginationMode="server"
              rowCount={totalRecords}
              paginationModel={paginationModel}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPaginationModelChange={(nextModel) => {
                if (nextModel.page === paginationModel.page && nextModel.pageSize === paginationModel.pageSize) {
                  return;
                }
                setPage(nextModel.page + 1);
                setPageSize(nextModel.pageSize);
              }}
              slots={{ toolbar: GridToolbar }}
              disableRowSelectionOnClick
              autoHeight
              rowHeight={92}
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
                '& .MuiDataGrid-cell': {
                  alignItems: 'center',
                  display: 'flex',
                },
              }}
            />
          </Paper>
        </Box>
      )}

      {view === 'eligible-users' && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                Eligible users
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Add users who are eligible to submit an Infinite application.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setIsBulkMarkEmailFlagDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Bulk mark email flags
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsEligibleUsersDialogOpen(true)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add eligible users
              </Button>
            </Stack>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search email"
              value={eligibilitiesEmailFilter}
              onChange={(event) => {
                setEligibilitiesEmailFilter(event.target.value);
                setEligibilitiesPage(1);
                setEligibilitySelectionModel([]);
              }}
              sx={{ minWidth: { md: 320 } }}
            />
            <TextField
              select
              label="Status"
              value={eligibilitiesStatusFilter}
              onChange={(event) => {
                setEligibilitiesStatusFilter(event.target.value as EligibilityStatusFilter);
                setEligibilitiesPage(1);
                setEligibilitySelectionModel([]);
              }}
              sx={{ minWidth: { md: 180 } }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="retracted">Retracted</MenuItem>
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
              {countLabel(selectedEligibilityRows.length, 'user')} selected
            </Typography>
            <Button
              variant="contained"
              disabled={selectedEligibilityRows.length === 0 || isUpdatingEligibility}
              onClick={() => startBulkEligibilityEmailUpdate('registerInterestEmailSent')}
            >
              Mark register interest email sent
            </Button>
            <Button
              variant="contained"
              disabled={selectedConciergeEligibleCount === 0 || isUpdatingEligibility}
              onClick={() => startBulkEligibilityEmailUpdate('conciergeIntroEmailSent')}
            >
              Mark concierge intro sent
            </Button>
            <Button
              variant="outlined"
              disabled={selectedEligibilityRows.length === 0 || isUpdatingEligibility}
              onClick={() => setEligibilitySelectionModel([])}
            >
              Clear selection
            </Button>
          </Stack>
          {eligibilitiesQuery.error instanceof Error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {eligibilitiesQuery.error.message}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ borderRadius: 2 }}>
            <DataGrid
              rows={eligibilityRows}
              columns={eligibilityColumns}
              loading={eligibilitiesQuery.isLoading || eligibilitiesQuery.isPlaceholderData}
              pagination
              paginationMode="server"
              rowCount={eligibilitiesTotalRecords}
              paginationModel={eligibilitiesPaginationModel}
              pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
              onPaginationModelChange={(nextModel) => {
                if (
                  nextModel.page === eligibilitiesPaginationModel.page &&
                  nextModel.pageSize === eligibilitiesPaginationModel.pageSize
                ) {
                  return;
                }
                setEligibilitiesPage(nextModel.page + 1);
                setEligibilitiesPageSize(nextModel.pageSize);
                setEligibilitySelectionModel((current) => (current.length === 0 ? current : []));
              }}
              checkboxSelection
              rowSelectionModel={eligibilitySelectionModel}
              onRowSelectionModelChange={setEligibilitySelectionModel}
              slots={{ toolbar: GridToolbar }}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
                '& .MuiDataGrid-cell': {
                  alignItems: 'center',
                  display: 'flex',
                },
              }}
            />
          </Paper>
          <EligibleUsersDialog
            open={isEligibleUsersDialogOpen}
            onClose={() => setIsEligibleUsersDialogOpen(false)}
            onCreated={() => {
              void queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-eligible-users'] });
            }}
          />
          <BulkMarkEmailFlagDialog
            open={isBulkMarkEmailFlagDialogOpen}
            onClose={() => setIsBulkMarkEmailFlagDialogOpen(false)}
            onCompleted={() => {
              void queryClient.invalidateQueries({ queryKey: ['lux-loyalty', 'infinite-eligible-users'] });
            }}
          />
          <EditEligibilityDialog
            eligibility={editingEligibility}
            open={Boolean(editingEligibility)}
            isSubmitting={updateEligibilityMutation.isPending}
            onClose={() => setEditingEligibility(null)}
            onSubmit={(eligibility, body) => {
              updateEligibilityMutation.mutate({ id: eligibility.id, body, closeEditor: true });
            }}
          />
          <AssociatedApplicationsDialog
            eligibility={associatedApplicationsEligibility}
            open={Boolean(associatedApplicationsEligibility)}
            onClose={() => setAssociatedApplicationsEligibility(null)}
            onReviewApplication={openReviewApplication}
          />
          <BulkEligibilityEmailConfirmDialog
            plan={pendingBulkEmailUpdate}
            open={Boolean(pendingBulkEmailUpdate)}
            isSubmitting={bulkUpdateEligibilityEmailFlagsMutation.isPending}
            onClose={() => setPendingBulkEmailUpdate(null)}
            onConfirm={(plan) => bulkUpdateEligibilityEmailFlagsMutation.mutate(plan)}
          />
        </Box>
      )}

      <ReviewApplicationDialog
        applicationId={reviewApplicationId}
        onClose={closeReviewApplication}
        onApprove={setPendingAction}
        onFinalize={finalizeApplication}
        onStartEnrolment={setEnrolmentRequest}
        isMutating={isMutating}
      />

      <InfiniteApplicationEnrolmentDialog
        request={enrolmentRequest}
        onClose={() => setEnrolmentRequest(null)}
        onCompleted={(request) => {
          void invalidateInfiniteApplicationReviewQueries(request.application.id);
        }}
      />

      <ApplicationActionConfirmDialog
        pendingAction={pendingAction}
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmApplicationAction}
        isMutating={isMutating}
      />
    </Box>
  );
}
